import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import './OrganizationsList.scss';

const OrganizationsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: '', search: '', page: 1 });
  const [showModal, setShowModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [viewingOrg, setViewingOrg] = useState(null);
  const [orgForAdmin, setOrgForAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
    planId: '',
    status: 'trial',
    address: { street: '', city: '', state: '', country: '', zipCode: '' },
    adminUser: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: ''
    }
  });
  const [adminFormData, setAdminFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: ''
  });
  const [formError, setFormError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['organizations', filters],
    queryFn: () => superAdminAPI.getOrganizations(filters)
  });

  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: superAdminAPI.getPlans
  });

  const organizations = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const plans = plansData?.data?.data || [];

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create organization')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminAPI.updateOrganization(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to update organization')
  });

  const suspendMutation = useMutation({
    mutationFn: superAdminAPI.suspendOrganization,
    onSuccess: () => queryClient.invalidateQueries(['organizations'])
  });

  const activateMutation = useMutation({
    mutationFn: superAdminAPI.activateOrganization,
    onSuccess: () => queryClient.invalidateQueries(['organizations'])
  });

  const createAdminMutation = useMutation({
    mutationFn: ({ orgId, data }) => superAdminAPI.createOrgAdmin(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['organizations']);
      closeAdminModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create admin')
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormError('');
    setFormData({
      name: '', slug: '', email: '', phone: '', planId: '', status: 'trial',
      address: { street: '', city: '', state: '', country: '', zipCode: '' },
      adminUser: { firstName: '', lastName: '', email: '', password: '', phone: '' }
    });
  };

  const closeAdminModal = () => {
    setShowAdminModal(false);
    setOrgForAdmin(null);
    setFormError('');
    setAdminFormData({
      firstName: '', lastName: '', email: '', password: '', phone: ''
    });
  };

  const openCreateAdminModal = (org) => {
    setOrgForAdmin(org);
    setShowAdminModal(true);
  };

  const openEditModal = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      slug: org.slug,
      email: org.email,
      phone: org.phone || '',
      planId: org.planId?._id || org.planId || '',
      status: org.status,
      address: org.address || { street: '', city: '', state: '', country: '', zipCode: '' },
      adminUser: { firstName: '', lastName: '', email: '', password: '', phone: '' }
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith('adminUser.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        adminUser: { ...prev.adminUser, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (name === 'name' && !editingOrg) {
        const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        setFormData(prev => ({ ...prev, slug }));
      }
    }
  };

  const handleAdminInputChange = (e) => {
    const { name, value } = e.target;
    setAdminFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.slug || !formData.email || !formData.planId) {
      setFormError('Please fill in all required organization fields');
      return;
    }

    if (!editingOrg) {
      const { firstName, lastName, email, password } = formData.adminUser;
      if (!firstName || !lastName || !email || !password) {
        setFormError('Please fill in all required admin user fields');
        return;
      }
      if (password.length < 8) {
        setFormError('Password must be at least 8 characters');
        return;
      }
    }

    if (editingOrg) {
      updateMutation.mutate({ id: editingOrg._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    const { firstName, lastName, email, password } = adminFormData;
    if (!firstName || !lastName || !email || !password) {
      setFormError('Please fill in all required fields');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    createAdminMutation.mutate({ orgId: orgForAdmin._id, data: adminFormData });
  };

  const handleSuspend = (orgId, orgName) => {
    if (window.confirm(`Are you sure you want to suspend "${orgName}"? This will block all access for this organization.`)) {
      suspendMutation.mutate(orgId);
    }
  };

  const handleActivate = (orgId, orgName) => {
    if (window.confirm(`Are you sure you want to activate "${orgName}"?`)) {
      activateMutation.mutate(orgId);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      trial: 'badge-warning',
      active: 'badge-success',
      suspended: 'badge-danger',
      cancelled: 'badge-secondary'
    };
    return classes[status] || 'badge-secondary';
  };

  return (
    <div className="organizations-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Organizations</h1>
          <p>Manage all organizations on the platform</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Organization
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search organizations..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="trial">Trial</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading organizations...</div>
        ) : organizations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No organizations found</h3>
            <p>Click "Add Organization" to create your first organization</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Admin</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org._id}>
                    <td>
                      <div className="org-name">
                        <span className="name">{org.name}</span>
                        <span className="slug">{org.slug}</span>
                      </div>
                    </td>
                    <td>{org.email}</td>
                    <td>
                      <span className="badge badge-info">
                        {org.planId?.name || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      {org.adminId ? (
                        <div className="admin-info">
                          <span className="admin-name">{org.adminId.firstName} {org.adminId.lastName}</span>
                          <span className="admin-email">{org.adminId.email}</span>
                        </div>
                      ) : (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => openCreateAdminModal(org)}
                        >
                          + Create Admin
                        </button>
                      )}
                    </td>
                    <td>{org.usage?.users || 0}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(org.status)}`}>
                        {org.status}
                      </span>
                    </td>
                    <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline" onClick={() => setViewingOrg(org)}>
                          View
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(org)}>
                          Edit
                        </button>
                        {org.status !== 'suspended' && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleSuspend(org._id, org.name)}
                          >
                            Suspend
                          </button>
                        )}
                        {org.status === 'suspended' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleActivate(org._id, org.name)}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span className="page-info">Page {filters.page} of {pagination.totalPages}</span>
          <button
            className="btn btn-outline"
            disabled={filters.page === pagination.totalPages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}

      {/* Create Admin Modal */}
      {showAdminModal && orgForAdmin && (
        <div className="modal-overlay" onClick={closeAdminModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Admin for {orgForAdmin.name}</h2>
              <button className="modal-close" onClick={closeAdminModal}>×</button>
            </div>

            <form onSubmit={handleAdminSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <p className="section-hint" style={{ marginBottom: '1.5rem' }}>
                This user will be the administrator for "{orgForAdmin.name}" and can log in to manage the organization.
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={adminFormData.firstName}
                    onChange={handleAdminInputChange}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={adminFormData.lastName}
                    onChange={handleAdminInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={adminFormData.email}
                    onChange={handleAdminInputChange}
                    placeholder="admin@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={adminFormData.phone}
                    onChange={handleAdminInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={adminFormData.password}
                  onChange={handleAdminInputChange}
                  placeholder="Minimum 8 characters"
                  required
                />
                <small className="form-hint">Password must be at least 8 characters long</small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeAdminModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createAdminMutation.isPending}
                >
                  {createAdminMutation.isPending ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Organization Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingOrg ? 'Edit Organization' : 'Add New Organization'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              {/* Organization Details Section */}
              <div className="form-section">
                <h4>Organization Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Acme Corporation"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug *</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="acme-corporation"
                      required
                      disabled={!!editingOrg}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="contact@acme.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Plan *</label>
                    <select name="planId" value={formData.planId} onChange={handleInputChange} required>
                      <option value="">Select a plan</option>
                      {plans.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.name} - ${plan.price?.monthly}/mo
                        </option>
                      ))}
                    </select>
                  </div>
                  {editingOrg && (
                    <div className="form-group">
                      <label>Status</label>
                      <select name="status" value={formData.status} onChange={handleInputChange}>
                        <option value="trial">Trial</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin User Section - Only show when creating */}
              {!editingOrg && (
                <div className="form-section admin-section">
                  <h4>Admin User Credentials</h4>
                  <p className="section-hint">This user will be the administrator for this organization and can log in to manage leads, users, and other data.</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="adminUser.firstName"
                        value={formData.adminUser.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="adminUser.lastName"
                        value={formData.adminUser.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Admin Email *</label>
                      <input
                        type="email"
                        name="adminUser.email"
                        value={formData.adminUser.email}
                        onChange={handleInputChange}
                        placeholder="admin@acme.com"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Phone</label>
                      <input
                        type="tel"
                        name="adminUser.phone"
                        value={formData.adminUser.phone}
                        onChange={handleInputChange}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      name="adminUser.password"
                      value={formData.adminUser.password}
                      onChange={handleInputChange}
                      placeholder="Minimum 8 characters"
                      required
                    />
                    <small className="form-hint">Password must be at least 8 characters long</small>
                  </div>
                </div>
              )}

              {/* Address Section */}
              <div className="form-section">
                <h4>Address (Optional)</h4>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      placeholder="NY"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      placeholder="United States"
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      placeholder="10001"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingOrg ? 'Update Organization' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingOrg && (
        <div className="modal-overlay" onClick={() => setViewingOrg(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Organization Details</h2>
              <button className="modal-close" onClick={() => setViewingOrg(null)}>×</button>
            </div>
            <div className="view-details">
              <div className="detail-section">
                <h4>Organization</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Name:</strong>
                    <span>{viewingOrg.name}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Slug:</strong>
                    <span>{viewingOrg.slug}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong>
                    <span>{viewingOrg.email}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Phone:</strong>
                    <span>{viewingOrg.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Plan:</strong>
                    <span>{viewingOrg.planId?.name || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <span className={`badge ${getStatusBadge(viewingOrg.status)}`}>
                      {viewingOrg.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Users:</strong>
                    <span>{viewingOrg.usage?.users || 0}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Created:</strong>
                    <span>{new Date(viewingOrg.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {viewingOrg.adminId ? (
                <div className="detail-section">
                  <h4>Admin User</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Name:</strong>
                      <span>{viewingOrg.adminId.firstName} {viewingOrg.adminId.lastName}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Email:</strong>
                      <span>{viewingOrg.adminId.email}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-section">
                  <h4>Admin User</h4>
                  <div className="no-admin-warning">
                    <p>⚠️ No admin user created yet</p>
                    <button
                      className="btn btn-warning"
                      onClick={() => { setViewingOrg(null); openCreateAdminModal(viewingOrg); }}
                    >
                      Create Admin User
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setViewingOrg(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewingOrg(null); openEditModal(viewingOrg); }}>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-large {
          max-width: 800px;
        }
        .form-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }
        .form-section h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 1rem;
        }
        .admin-section {
          background: #f9fafb;
          margin: 1.5rem -1.5rem 0;
          padding: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }
        .section-hint {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 1rem;
        }
        .form-hint {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }
        .admin-info {
          display: flex;
          flex-direction: column;
        }
        .admin-name {
          font-weight: 500;
        }
        .admin-email {
          font-size: 0.75rem;
          color: #6b7280;
        }
        .no-admin {
          color: #9ca3af;
          font-style: italic;
          font-size: 0.875rem;
        }
        .btn-warning {
          background: #f59e0b;
          color: white;
          font-size: 0.75rem;
        }
        .btn-warning:hover:not(:disabled) {
          background: #d97706;
        }
        .view-details {
          padding: 1.5rem;
        }
        .detail-section {
          margin-bottom: 1.5rem;
        }
        .detail-section h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .detail-item strong {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }
        .detail-item span {
          font-size: 0.875rem;
          color: #111827;
        }
        .no-admin-warning {
          background: #fef3c7;
          padding: 1rem;
          border-radius: 0.5rem;
          text-align: center;
        }
        .no-admin-warning p {
          color: #92400e;
          margin-bottom: 0.75rem;
        }
        @media (max-width: 640px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default OrganizationsList;